import { useMemo } from "react";
import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { MarkdownReact, MESSAGE_PROSE_CLASS } from "@/components/MarkdownReact";
import { AssetDisplay } from "@/components/AssetDisplay";
import {
  SendRichMessageInputSchema,
  type OutputAsset,
} from "@forgepoint/shared";
import type { MessageNode } from "./utils";
import { extractMessageContent, isSendRichMessageToolCall } from "./utils";
import { extractToolCallError } from "@/modules/agents/components/helpers/richActionHelpers";
import { TIMELINE_ENTER_ANIMATION_CLASS } from "./TimelineRailItem";

interface TimelineProseMessageProps {
  node: MessageNode;
  isThreadActive: boolean;
}

/**
 * An intermediate message on the working layer, rendered as flat prose: full
 * markdown and any assets, with no fold and no rail. Handles both plain
 * messages (any role, so a sub-agent's user-role instructions render the same
 * way) and send_rich_message tool calls; a failed rich message keeps its prose
 * and gains a quiet failure note.
 */
export function TimelineProseMessage({
  node,
  isThreadActive,
}: TimelineProseMessageProps) {
  const msg = node.message.message;

  const richParts = useMemo(() => {
    if (!isSendRichMessageToolCall(msg)) {
      return null;
    }
    const parsed = SendRichMessageInputSchema.safeParse(msg.arguments);
    return parsed.success ? parsed.data.parts : null;
  }, [msg]);

  const text = extractMessageContent(msg);
  const assets = node.message.assets ?? [];
  const sendError = isSendRichMessageToolCall(msg)
    ? extractToolCallError(node.toolResult)
    : null;

  // Guards against an empty padded block, e.g. a rich message whose arguments
  // failed to parse.
  const hasContent =
    (richParts?.length ?? 0) > 0 || text.trim().length > 0 || assets.length > 0;
  if (!hasContent && !sendError) {
    return null;
  }

  return (
    // -ml cancels the rail container's indent (9px margin + 16px padding) so
    // prose sits flush with the section header, left of the rail rows.
    <div
      className={cn(
        "-ml-[25px] space-y-2 py-2 pr-1.5",
        isThreadActive && TIMELINE_ENTER_ANIMATION_CLASS,
      )}
    >
      {sendError && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 text-danger" />
          Message failed to send
        </div>
      )}
      {richParts ? (
        richParts.map((part, index) => {
          if (part.type === "text" && part.text) {
            return (
              <div key={index} className={MESSAGE_PROSE_CLASS}>
                <MarkdownReact>{part.text}</MarkdownReact>
              </div>
            );
          }
          if (part.type === "assets" && part.asset_ids) {
            const resolved = resolveAssets(part.asset_ids, assets);
            return resolved.length > 0 ? (
              <AssetDisplay key={index} assets={resolved} />
            ) : null;
          }
          return null;
        })
      ) : (
        <>
          <div className={MESSAGE_PROSE_CLASS}>
            <MarkdownReact>{text}</MarkdownReact>
          </div>
          {assets.length > 0 && <AssetDisplay assets={assets} />}
        </>
      )}
    </div>
  );
}

function resolveAssets(
  assetIds: string[],
  assets: OutputAsset[],
): OutputAsset[] {
  const byId = new Map(assets.map((asset) => [asset.id, asset]));
  return assetIds
    .map((id) => byId.get(id))
    .filter((asset): asset is OutputAsset => asset !== undefined);
}
